import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { vehicleService } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Plus, Car as CarIcon, Edit, Trash2, Eye, EyeOff, Archive, BarChart2, TrendingUp } from 'lucide-react';
import { toast } from 'sonner';

const statusLabels = {
  draft: { label: 'Borrador', variant: 'secondary' },
  published: { label: 'Publicado', variant: 'default' },
  reserved: { label: 'Reservado', variant: 'outline' },
  sold: { label: 'Vendido', variant: 'destructive' },
  archived: { label: 'Archivado', variant: 'secondary' }
};

const CarsListPage = () => {
  const { canManageVehicles } = useAuth();
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all' | 'published' | 'archived'
  const [confirmDelete, setConfirmDelete] = useState(null); // vehicle id pending confirm
  const [activeStatsId, setActiveStatsId] = useState(null);
  const [statsCache, setStatsCache] = useState({});
  const [statsLoading, setStatsLoading] = useState(false);
  const statsRef = useRef(null);

  useEffect(() => {
    loadVehicles();
  }, []);

  useEffect(() => {
    if (!activeStatsId) return;
    const handleClickOutside = (e) => {
      if (statsRef.current && !statsRef.current.contains(e.target)) {
        setActiveStatsId(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [activeStatsId]);

  const loadVehicles = async () => {
    try {
      const data = await vehicleService.getAll();
      setVehicles(data);
    } catch (error) {
      console.error('Error loading vehicles:', error);
      toast.error('Error al cargar vehículos');
    } finally {
      setLoading(false);
    }
  };

  const handleArchive = async (vehicle) => {
    try {
      await vehicleService.updateStatus(vehicle.id, 'archived');
      toast.success('Vehículo archivado');
      loadVehicles();
    } catch (error) {
      toast.error('Error al archivar');
    }
  };

  const handleDelete = async (id) => {
    try {
      await vehicleService.delete(id, true); // hard_delete = true
      toast.success('Vehículo eliminado permanentemente');
      setConfirmDelete(null);
      loadVehicles();
    } catch (error) {
      toast.error('Error al eliminar');
    }
  };

  const togglePublish = async (vehicle) => {
    try {
      const newStatus = vehicle.status === 'published' ? 'draft' : 'published';
      await vehicleService.updateStatus(vehicle.id, newStatus);
      toast.success(newStatus === 'published' ? 'Vehículo publicado' : 'Vehículo despublicado');
      loadVehicles();
    } catch (error) {
      toast.error('Error al cambiar estado');
    }
  };

  const handleStats = async (vehicleId) => {
    if (activeStatsId === vehicleId) {
      setActiveStatsId(null);
      return;
    }
    setActiveStatsId(vehicleId);
    if (!statsCache[vehicleId]) {
      setStatsLoading(true);
      try {
        const stats = await vehicleService.getStats(vehicleId);
        setStatsCache(prev => ({ ...prev, [vehicleId]: stats }));
      } catch {
        toast.error('Error al cargar estadísticas');
        setActiveStatsId(null);
      } finally {
        setStatsLoading(false);
      }
    }
  };

  const filteredVehicles = vehicles.filter((v) => {
    if (filter === 'published') return v.status === 'published';
    if (filter === 'archived') return v.status === 'archived';
    return true; // 'all'
  });

  const formatPrice = (pricing) => {
    if (!pricing) return 'N/A';
    return `${pricing.currency} $${pricing.price?.toLocaleString() || 0}`;
  };

  return (
    <div>
      {/* Modal de confirmación de eliminación */}
      {confirmDelete && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl p-6 max-w-sm w-full shadow-xl">
            <h2 className="text-lg font-bold text-slate-900 mb-2">Eliminar vehículo</h2>
            <p className="text-slate-600 mb-6">
              Esta acción es <span className="font-semibold text-red-600">permanente</span> y no se puede deshacer. ¿Confirmás?
            </p>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setConfirmDelete(null)}>
                Cancelar
              </Button>
              <Button variant="destructive" className="flex-1" onClick={() => handleDelete(confirmDelete)}>
                Sí, eliminar
              </Button>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4 mb-6">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-slate-900">Vehículos</h1>
          <p className="text-slate-600 mt-2">Gestiona tu inventario de vehículos</p>
        </div>
        {canManageVehicles && (
          <Link to="/app/cars/new">
            <Button data-testid="new-car-button">
              <Plus className="w-4 h-4 mr-2" />
              Nuevo Vehículo
            </Button>
          </Link>
        )}
      </div>

      {/* Filtros */}
      <div className="flex gap-2 mb-6">
        {[
          { key: 'all', label: 'Todos' },
          { key: 'published', label: 'Publicados' },
          { key: 'archived', label: 'Archivados' },
        ].map((tab) => (
          <button
            key={tab.key}
            onClick={() => setFilter(tab.key)}
            className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors ${
              filter === tab.key
                ? 'bg-slate-900 text-white'
                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="text-center py-12">Cargando...</div>
      ) : filteredVehicles.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <CarIcon className="w-16 h-16 mx-auto text-slate-300 mb-4" />
            <p className="text-slate-600 mb-4">
              {vehicles.length === 0
                ? 'Aún no tenés vehículos registrados'
                : 'No hay vehículos en este filtro'}
            </p>
            {canManageVehicles && vehicles.length === 0 && (
              <Link to="/app/cars/new">
                <Button>Crear primer vehículo</Button>
              </Link>
            )}
          </CardContent>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredVehicles.map((vehicle) => {
            const statusInfo = statusLabels[vehicle.status] || statusLabels.draft;
            const vehicleData = vehicle.vehicle || {};
            const media = vehicle.media || {};
            
            return (
              <Card key={vehicle.id} data-testid={`vehicle-card-${vehicle.id}`}>
                <CardHeader>
                  <div className="flex justify-between items-start">
                    <div>
                      <CardTitle className="text-lg">
                        {vehicleData.make} {vehicleData.model}
                      </CardTitle>
                      <p className="text-sm text-slate-500 mt-1">
                        {vehicleData.year} • {vehicleData.kilometers?.toLocaleString() || 0} km
                      </p>
                    </div>
                    <Badge variant={statusInfo.variant}>
                      {statusInfo.label}
                    </Badge>
                  </div>
                </CardHeader>
                <CardContent>
                  {media.coverUrl ? (
                    <img
                      src={media.coverUrl}
                      alt={`${vehicleData.make} ${vehicleData.model}`}
                      className="w-full h-40 object-cover rounded-lg mb-4"
                    />
                  ) : (
                    <div className="w-full h-40 bg-slate-200 rounded-lg mb-4 flex items-center justify-center">
                      <CarIcon className="w-12 h-12 text-slate-400" />
                    </div>
                  )}

                  <div className="mb-4">
                    <p className="text-2xl font-bold text-slate-900">
                      {formatPrice(vehicle.pricing)}
                    </p>
                    <div className="flex gap-2 mt-2">
                      <Badge variant="outline">{vehicleData.fuel || 'N/A'}</Badge>
                      <Badge variant="outline">{vehicleData.transmission || 'N/A'}</Badge>
                    </div>
                  </div>

                  {canManageVehicles && (
                    <div className="flex gap-2">
                      <Link to={`/app/cars/${vehicle.id}/edit`} className="flex-1">
                        <Button variant="outline" size="sm" className="w-full">
                          <Edit className="w-4 h-4 mr-1" />
                          Editar
                        </Button>
                      </Link>
                      {/* Publicar / Despublicar */}
                      <Button
                        variant="outline"
                        size="sm"
                        title={vehicle.status === 'published' ? 'Despublicar' : 'Publicar'}
                        onClick={() => togglePublish(vehicle)}
                      >
                        {vehicle.status === 'published' ? (
                          <EyeOff className="w-4 h-4" />
                        ) : (
                          <Eye className="w-4 h-4" />
                        )}
                      </Button>
                      {/* Archivar (solo si no está archivado) */}
                      {vehicle.status !== 'archived' && (
                        <Button
                          variant="outline"
                          size="sm"
                          title="Archivar"
                          onClick={() => handleArchive(vehicle)}
                        >
                          <Archive className="w-4 h-4" />
                        </Button>
                      )}
                      {/* Estadísticas */}
                      <div
                        className="relative"
                        ref={activeStatsId === vehicle.id ? statsRef : null}
                      >
                        <Button
                          variant="outline"
                          size="sm"
                          title="Estadísticas"
                          onClick={() => handleStats(vehicle.id)}
                        >
                          <BarChart2 className="w-4 h-4" />
                        </Button>
                        {activeStatsId === vehicle.id && (
                          <div className="absolute bottom-full right-0 mb-2 w-56 bg-white border border-slate-200 rounded-xl shadow-xl p-3 z-50">
                            {statsLoading && !statsCache[vehicle.id] ? (
                              <p className="text-xs text-center text-slate-500 py-2">Cargando...</p>
                            ) : statsCache[vehicle.id] ? (
                              <>
                                <div className="flex items-center gap-2 mb-1.5">
                                  <Eye className="w-3.5 h-3.5 text-slate-400" />
                                  <span className="text-xs text-slate-700">
                                    <strong>{statsCache[vehicle.id].total}</strong> visualizaciones
                                  </span>
                                </div>
                                <div className="flex items-center gap-2 mb-3">
                                  <TrendingUp className="w-3.5 h-3.5 text-slate-400" />
                                  <span className="text-xs text-slate-700">
                                    <strong>{statsCache[vehicle.id].interestRate}%</strong> del tráfico
                                  </span>
                                </div>
                                <p className="text-[10px] text-slate-400 mb-1">Últimos 7 días</p>
                                <div className="flex items-end gap-0.5" style={{ height: '40px' }}>
                                  {(() => {
                                    const days = statsCache[vehicle.id].last7days;
                                    const maxCount = Math.max(...days.map(d => d.count), 1);
                                    return days.map((day) => (
                                      <div
                                        key={day.date}
                                        className="flex-1 bg-slate-700 rounded-sm"
                                        style={{ height: `${Math.max((day.count / maxCount) * 100, 4)}%` }}
                                        title={`${day.date}: ${day.count}`}
                                      />
                                    ));
                                  })()}
                                </div>
                                <div className="flex justify-between mt-1">
                                  <span className="text-[9px] text-slate-400">
                                    {statsCache[vehicle.id].last7days[0]?.date.slice(5)}
                                  </span>
                                  <span className="text-[9px] text-slate-400">
                                    {statsCache[vehicle.id].last7days[6]?.date.slice(5)}
                                  </span>
                                </div>
                              </>
                            ) : null}
                          </div>
                        )}
                      </div>
                      {/* Eliminar permanentemente */}
                      <Button
                        variant="destructive"
                        size="sm"
                        title="Eliminar permanentemente"
                        onClick={() => setConfirmDelete(vehicle.id)}
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default CarsListPage;
